/**
 * Admin Login Page — same dark/green design language as the marketing site
 * (chatbase/frontend/src/pages/home/marketing-shell.tsx), now theme-aware
 * (light default, toggled via the shared NavBar) and rendered with the same
 * NavBar/Footer chrome every other onboarding page uses (Signup, Onboarding,
 * GoogleConfirm). Previously fixed dark-only with no navbar at all — this
 * was the one page in the funnel that didn't match the rest.
 */
import { useAuth, isSafeInternalPath } from '@/hooks/useAuth';
import GoogleSignInButton, { isGoogleSignInConfigured } from '@/components/auth/GoogleSignInButton';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '@/pages/home/marketing-shell';
import { MailOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { MessageSquare } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { formatWait, useCooldown } from '@/hooks/useCooldown';

/** Shared with the forgot/reset password pages so all three fields look alike. */
export function inputSx(t: (typeof THEMES)['dark']) {
  return {
    '& .MuiOutlinedInput-root': {
      background: t.muted,
      borderRadius: '10px',
      color: t.fg,
      '& fieldset': { borderColor: t.border },
      '&:hover fieldset': { borderColor: t.green },
      '&.Mui-focused fieldset': { borderColor: t.green, borderWidth: 1.5 },
    },
    '& .MuiInputBase-input': {
      color: t.fg,
      caretColor: t.fg,
      // Chrome autofill forces its own light background + dark text via a
      // huge inset box-shadow — override both, plus WebKit's separate
      // text-fill-color (color alone doesn't affect autofilled text).
      '&:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${t.muted} inset`,
        WebkitTextFillColor: t.fg,
        caretColor: t.fg,
        borderRadius: '10px',
        transition: 'background-color 9999s ease-in-out 0s',
      },
      '&:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${t.muted} inset`,
        WebkitTextFillColor: t.fg,
      },
    },
    '& .MuiInputBase-input::placeholder': { color: t.mutedFg, opacity: 1 },
  };
}

function LoginForm() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const { login, googlePreview, isAuthenticated, loading: authLoading, user, tenant } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by e.g. the invite-accept flow ("log in to accept this invitation")
  // so a successful login lands back where the visitor actually wanted to
  // go instead of always the dashboard — see pages/invite/InviteAccept.tsx.
  const redirectParam = searchParams.get('redirect') || undefined;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Sign-in lockout (429) — mirrors the server's Retry-After on the button.
  const lockout = useCooldown();
  // Set by ResetPassword after a successful reset.
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;

  // Redirect if already authenticated. No tenant yet (registered/invited but
  // never finished onboarding) always goes to /onboarding — never a
  // dashboard route that assumes tenant context exists. redirectParam is
  // validated (isSafeInternalPath — rejects protocol-relative/non-relative
  // values before they ever reach navigate()) AND only honored once a
  // tenant exists: a tenant-less visitor carrying a deep link to a
  // tenant-scoped page would otherwise skip straight past the "no tenant"
  // branch below and hit that page's own ProtectedRoute guard a second
  // time, bouncing to /onboarding anyway — landing them on /onboarding
  // directly, the same place they'd end up either way, is simpler and
  // avoids that extra round trip. /invite is the one exception: it's a
  // public page built specifically to handle a tenant-less authenticated
  // session (see InviteAccept.tsx's sessionMatches branch — a "log in to
  // accept" identity with no org yet needs to land back there to click
  // Join, not get diverted into creating its own org). Must stay consistent
  // with useAuth.ts's computeDestination(), which already checks
  // redirectTo before tenant for the same reason.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const isInviteRedirect = isSafeInternalPath(redirectParam) && redirectParam.startsWith('/invite');
      if (!tenant && !isInviteRedirect) {
        navigate('/onboarding', { replace: true });
        return;
      }
      if (isSafeInternalPath(redirectParam)) {
        navigate(redirectParam, { replace: true });
        return;
      }
      const role = user?.role;
      if (role === 'tenant_owner' || role === 'superadmin' || role === 'tenant_admin') {
        navigate('/admin/welcome', { replace: true });
      } else {
        navigate('/welcome', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, navigate, user, tenant, redirectParam]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password, redirectParam);
    if (!result.success) {
      setError(result.error || 'Login failed. Please check your credentials.');
      if ('retryAfter' in result && result.retryAfter) lockout.start(result.retryAfter);
      setLoading(false);
    }
  };

  // Carries the typed email over so the user doesn't retype it. Router state,
  // not a query param — an email address never goes into a URL.
  const goToForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/forgot-password', { state: { email: email.trim() } });
  };

  const handleGoogleSuccess = async (idToken: string) => {
    setError('');
    setLoading(true);
    const result = await googlePreview(idToken);
    if (!result.success) {
      setError(result.error || 'Google sign-in failed');
      setLoading(false);
      return;
    }
    // A known identity is already logged in at this point. A brand-new one
    // still needs to review and confirm the data before an account is
    // created — hand off instead of asserting it on their behalf.
    if (result.isNewIdentity) {
      navigate('/auth/google/confirm', {
        state: { googleTicket: result.googleTicket, preview: result.preview },
      });
      return;
    }
    setLoading(false);
  };

  const fieldSx = inputSx(t);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* ── Left panel (illustration / brand) ─────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '50%',
          background: isDark
            ? 'radial-gradient(ellipse 100% 70% at 50% 30%, rgba(61,220,132,0.12) 0%, transparent 60%), linear-gradient(145deg, #0D2318 0%, #06090A 60%, #0D2318 100%)'
            : 'radial-gradient(ellipse 100% 70% at 50% 30%, rgba(37,211,102,0.14) 0%, transparent 60%), linear-gradient(145deg, #E9FBF0 0%, #F4FFF8 60%, #E9FBF0 100%)',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        {[260, 400, 540].map((size, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: '50%',
              border: `1px solid ${isDark ? 'rgba(61,220,132,0.12)' : 'rgba(37,211,102,0.18)'}`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          />
        ))}

        <Box sx={{ position: 'relative', textAlign: 'center' }}>
          {/* Logo mark */}
          <Box
            component="img"
            src="/logo.png"
            alt="Baatly"
            sx={{
              width: 72,
              height: 72,
              objectFit: 'contain',
              mx: 'auto',
              mb: 4,
              display: 'block',
              filter: 'drop-shadow(0 0 30px rgba(61,220,132,0.35))',
            }}
          />

          <Typography
            sx={{
              color: t.fg,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              mb: 2,
              fontFamily: 'Manrope, sans-serif',
              fontSize: '2.25rem',
            }}
          >
            Baat<span style={{ color: t.green }}>ly</span>
          </Typography>
          <Typography sx={{ color: t.textSecondary, maxWidth: 340, mx: 'auto', lineHeight: 1.7 }}>
            Manage your AI agents, knowledge sources, and conversation analytics — all in one place.
          </Typography>

          {/* Feature pills */}
          <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {['AI Agents', 'Chat Analytics', 'Smart Sources', 'Lead Management'].map((f) => (
              <Box
                key={f}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: 99,
                  bgcolor: t.greenDim,
                  border: `1px solid ${t.border}`,
                }}
              >
                <Typography sx={{ color: t.green, fontSize: '0.8125rem', fontWeight: 600 }}>{f}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Right panel (form) ─────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile brand */}
          <Box sx={{ display: { md: 'none' }, mb: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2,
                bgcolor: t.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <MessageSquare size={24} color={isDark ? '#050C09' : '#fff'} strokeWidth={2.5} />
            </Box>
            <Typography
              fontWeight={800}
              sx={{
                letterSpacing: '-0.02em',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '1.5rem',
                color: t.fg,
              }}
            >
              Baat<span style={{ color: t.green }}>ly</span>
            </Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              mb: 1,
              letterSpacing: '-0.02em',
              fontFamily: 'Manrope, sans-serif',
              fontSize: '1.75rem',
              color: t.fg,
            }}
          >
            Sign in
          </Typography>
          <Typography variant="body2" sx={{ color: t.mutedFg, mb: 3 }}>
            Sign in to your admin dashboard
          </Typography>

          <Divider sx={{ mb: 3, borderColor: t.border }} />

          {isGoogleSignInConfigured && (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <GoogleSignInButton onSuccess={handleGoogleSuccess} width={368} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: t.border }} />
                <Typography sx={{ fontSize: 12, color: t.mutedFg, fontWeight: 600 }}>OR</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: t.border }} />
              </Box>
            </>
          )}

          {notice && !error && (
            <Alert severity="success" sx={{ mb: 2.5 }}>
              {notice}
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                bgcolor: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                color: '#ef4444',
                '& .MuiAlert-icon': { color: '#ef4444' },
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} noValidate>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                mb: 0.75,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: t.mutedFg,
              }}
            >
              Email
            </Typography>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              sx={{ mb: 2, ...fieldSx }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline sx={{ fontSize: 18, color: t.mutedFg }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: t.mutedFg,
                }}
              >
                Password
              </Typography>
              <Box
                component="a"
                href="/forgot-password"
                onClick={goToForgotPassword}
                sx={{
                  color: t.green,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                Forgot password?
              </Box>
            </Box>
            <TextField
              fullWidth
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Your password"
              sx={{ mb: 3, ...fieldSx }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPass((p) => !p)}
                      edge="end"
                      aria-label={showPass ? 'Hide password' : 'Show password'}
                    >
                      {showPass ? (
                        <VisibilityOff sx={{ fontSize: 18, color: t.mutedFg }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: t.mutedFg }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || lockout.active || !email || !password}
              sx={{
                py: 1.5,
                fontSize: '0.9375rem',
                fontWeight: 700,
                fontFamily: 'Manrope, sans-serif',
                borderRadius: '10px',
                background: t.green,
                color: isDark ? '#050C09' : '#fff',
                boxShadow: `0 6px 24px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
                '&:hover': {
                  background: isDark ? '#34c476' : '#1fb955',
                  boxShadow: `0 8px 30px ${isDark ? 'rgba(61,220,132,0.5)' : 'rgba(37,211,102,0.55)'}`,
                },
                '&:disabled': { opacity: 0.5, color: isDark ? '#050C09' : '#fff' },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : lockout.active ? (
                `Try again in ${formatWait(lockout.remaining)}`
              ) : (
                'Sign in to Dashboard'
              )}
            </Button>
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '0.875rem', color: t.mutedFg }}>
            New to Baatly?{' '}
            <Box
              component="a"
              href="/signup"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                navigate('/signup');
              }}
              sx={{ color: t.green, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}
            >
              Create a free account
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function Login() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main style={{ paddingTop: 64 }}>
          <LoginForm />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
